import React, { useEffect, useState } from "react";
import {
  RiTeamLine,
  RiUserFollowLine,
  RiExchangeDollarLine,
  RiSettings4Line,
} from "react-icons/ri";
import { Sidebar, Menu, MenuItem } from "react-pro-sidebar";
import InfoBox from "../../Common/InfoBox/infoBox";
import { useLocation, useNavigate } from "react-router";
import styles from "./SideBar.module.css";
import { useRoleFeature } from "../../context/RoleFeatureContext";
import { Spinner } from "react-bootstrap";

function SideBar() {
  const { roleFeatureList } = useRoleFeature();
  const [collapsed, setCollapsed] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const getRouteMapping = () => ({
    "INVESTING-Customers": { link: "/customer", product: "INVESTING" },
    "INVESTING-Reports": { link: "/report", product: "INVESTING" },
    "INVESTING-Orders": { link: "/orders", product: "INVESTING" },
    "INVESTING-Partners": { link: "/partners", product: "INVESTING" },
    "INVESTING-Plans": { link: "/Investing-Plan", product: "INVESTING" },
    "INVESTING-Coupons": { link: "/Investing-Coupons", product: "INVESTING" },

    "EASYREMIT-Users": { link: "/easy-remit", product: "EASYREMIT" },
    "EASYREMIT-Recent Transaction": {
      link: "/recent-transaction",
      product: "EASYREMIT",
    },
    "EASYREMIT-Coupons": {
      link: "/coupons",
      product: "EASYREMIT",
      subLink: ["/edit-coupon/", "/create-coupon"],
    },
    "EASYREMIT-Partners": { link: "/Create-Partner", product: "EASYREMIT" },
    "EASYREMIT-Plans": { link: "/Create-Plan", product: "EASYREMIT" },

    "MCB-Users": { link: "/mcb", product: "MCB" },
    "MCB-Transactions": { link: "/transactions", product: "MCB" },
    "MCB-Reports": { link: "/user-reports", product: "MCB" },
    "MCB-Partner Rates": { link: "/partner-rates", product: "MCB" },
    "MCB-Partner Beneficiaries": { link: "/partner-beneficiaries", product: "MCB" },
    "MCB-Beneficiary Review": { link: "/beneficiary", product: "MCB" },

    "ADMIN-Users": { link: "/Users", product: "ADMIN" },
    "ADMIN-Create User": { link: "/create-user", product: "ADMIN" },
    "ADMIN-Add Features": { link: "/add-features", product: "ADMIN" },
    "ADMIN-Add Role": { link: "/add-role", product: "ADMIN" },
    "ADMIN-Activity Logger": { link: "/activity-logger", product: "ADMIN" },
    "ADMIN-Event Management": { link: "/event-management", product: "ADMIN" },

  });

  const getOrganizedFeatures = () => {
    if (!roleFeatureList?.features) {
      return {
        investing: [],
        easyremit: [],
        mcb: [],
        admin: [],
      };
    }

    const investing = [];
    const easyremit = [];
    const mcb = [];
    const admin = [];

    const routeMapping = getRouteMapping();

    roleFeatureList.features.forEach(({ source, featureList }) => {
      featureList.forEach((feature) => {
        const routeKey = `${source}-${feature.componentName}`;
        const routeInfo = routeMapping[routeKey];

        if (routeInfo) {
          const menuItem = {
            name: feature.componentName,
            nameToShow: feature.componentName,
            link: routeInfo.link,
            product: source,
            subLink: routeInfo.subLink || [],
            readAccess: feature.readAccess,
            writeAccess: feature.writeAccess,
            modifyAccess: feature.modifyAccess,
          };

          if (feature.readAccess) {
            switch (source) {
              case "INVESTING":
                investing.push(menuItem);
                break;
              case "EASYREMIT":
                easyremit.push(menuItem);
                break;
              case "MCB":
                mcb.push(menuItem);
                break;
              case "ADMIN":
                admin.push(menuItem);
                break;
            }
          }
        }
      });
    });

    return { investing, easyremit, mcb, admin };
  };

  const organizedFeatures = getOrganizedFeatures();

  const arr = [
    {
      icon: <RiTeamLine />,
      title: "Investing",
      item: organizedFeatures.investing,
      key: "INVESTING",
    },
    {
      icon: <RiUserFollowLine />,
      title: "Borderless Pe",
      item: organizedFeatures.easyremit,
      key: "EASYREMIT",
    },
    {
      icon: <RiExchangeDollarLine />,
      title: "MCA",
      item: organizedFeatures.mcb,
      key: "MCB",
    },
    {
      icon: <RiSettings4Line />,
      title: "Admin Panel",
      item: organizedFeatures.admin,
      key: "ADMIN",
    },
  ];

  const handleCollapsedChange = () => {
    setCollapsed(!collapsed);
  };

  const handleToggleSidebar = (value) => {
    setToggled(value);
  };

  const handleSelectProduct = (item) => {
    setSelectedMenu(item);
    if (item.item && item.item.length > 0) {
      navigate(item.item[0]?.link);
    }
  };

  const findProductByRoute = (pathname) => {
    const allFeatures = [
      ...organizedFeatures.investing,
      ...organizedFeatures.easyremit,
      ...organizedFeatures.mcb,
      ...organizedFeatures.admin,
    ];

    for (const feature of allFeatures) {
      if (
        pathname === feature.link ||
        pathname.startsWith(feature.link) ||
        feature.subLink?.some((sub) => pathname.includes(sub))
      ) {
        return feature.product;
      }
    }

    return null;
  };

  useEffect(() => {
    const features = getOrganizedFeatures();

    if (location.pathname === "/") {
      if (features.investing.length > 0) {
        navigate(features.investing[0]?.link);
        setSelectedMenu(arr.find((item) => item.key === "INVESTING"));
      } else if (features.easyremit.length > 0) {
        navigate(features.easyremit[0]?.link);
        setSelectedMenu(arr.find((item) => item.key === "EASYREMIT"));
      } else if (features.mcb.length > 0) {
        navigate(features.mcb[0]?.link);
        setSelectedMenu(arr.find((item) => item.key === "MCB"));
      } else if (features.admin.length > 0) {
        navigate(features.admin[0]?.link);
        setSelectedMenu(arr.find((item) => item.key === "ADMIN"));
      }
    } else {
      const activeProduct = findProductByRoute(location.pathname);
      const activeMenuItem = arr.find((item) => item.key === activeProduct);
      if (activeMenuItem) {
        setSelectedMenu(activeMenuItem);
      }
    }
  }, [roleFeatureList, location.pathname, navigate]);
  if (loading) {
    return <Spinner animation="border" className={styles.spinnerColor} />;
  }

  return (
    <Sidebar
      className={`app ${toggled ? "toggled" : "sideBar"}`}
      style={{
        height: "100vh",
        width: "280px",
        position: "absolute",
        paddingTop: "64px",
      }}
      collapsed={collapsed}
      toggled={toggled}
      handleToggleSidebar={handleToggleSidebar}
      handleCollapsedChange={handleCollapsedChange}
    >
      <main>
        <Menu>
          {collapsed ? (
            <MenuItem onClick={handleCollapsedChange}></MenuItem>
          ) : (
            <div style={{ display: "flex" }}>
              <div className={styles.productmenu}>
                {arr.map((item) => (
                  <React.Fragment key={item.title}>
                    {item.item.length > 0 && (
                      <InfoBox content={item.title}>
                        <div
                          className={styles.productdiv}
                          style={{ margin: "5px 15px 5px 15px" }}
                          onClick={() => handleSelectProduct(item)}
                        >
                          <div
                            className={
                              selectedMenu?.key === item.key
                                ? styles.ProductActive
                                : styles.productInActive
                            }
                          >
                            {item.icon}
                          </div>
                        </div>
                      </InfoBox>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <div className={styles.menuText}>
                <h6 className={styles.SelectedHeading}>
                  {selectedMenu?.title}
                </h6>
                {selectedMenu?.item.map((item, index) => {
                  const isActive =
                    location.pathname === item?.link ||
                    location.pathname.startsWith(item?.link) ||
                    item?.subLink?.some((sub) =>
                      location.pathname.includes(sub)
                    );
                  return (
                    <MenuItem
                      key={`${item.product}-${item.name}-${index}`}
                      onClick={() => navigate(item?.link)}
                      style={{
                        backgroundColor: isActive
                          ? "rgba(222, 222, 222, 0.136)"
                          : "",
                      }}
                    >
                      {item.nameToShow}
                    </MenuItem>
                  );
                })}
              </div>
            </div>
          )}
        </Menu>
      </main>
    </Sidebar>
  );
}
export default SideBar;
